import React, { useState, useRef, useEffect } from 'react';
import {
    FiCalendar,
    FiClock,
    FiMapPin,
    FiInfo,
    FiX,
    FiUser,
    FiMail,
    FiPhone,
    FiUsers,
    FiMessageSquare,
    FiCheckCircle,
    FiAlertCircle,
    FiChevronRight,
    FiPlus,
    FiMinus,
    FiStar,
} from 'react-icons/fi';
import './PackageDetailsPage.css';
import api from "../../../Api";

export default function BookingSection({ packageData, destinationName, mealPlans, specialRequests }) {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [step, setStep] = useState(1);

    const [selectedMealPlan, setSelectedMealPlan] = useState(null);
    const [selectedSpecialRequests, setSelectedSpecialRequests] = useState([]);
    const [selectedVegItems, setSelectedVegItems] = useState([]);
    const [selectedNonVegItems, setSelectedNonVegItems] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const modalRef = useRef(null);

    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        whatsapp: '',
        guests: '',
        date: '',
        notes: '',
    });

    // Restore booking state after login redirect
    useEffect(() => {
        const saved = sessionStorage.getItem('bookingRedirect');
        if (saved) {
            try {
                const savedState = JSON.parse(saved);
                if (savedState.shouldOpenModal && savedState.packageId === packageData?.id) {
                    if (savedState.formData) setFormData(savedState.formData);
                    if (savedState.selectedMealPlan) setSelectedMealPlan(savedState.selectedMealPlan);
                    if (savedState.selectedSpecialRequests) setSelectedSpecialRequests(savedState.selectedSpecialRequests);
                    if (savedState.selectedVegItems) setSelectedVegItems(savedState.selectedVegItems);
                    if (savedState.selectedNonVegItems) setSelectedNonVegItems(savedState.selectedNonVegItems);
                    setIsModalOpen(true);
                }
                sessionStorage.removeItem('bookingRedirect');
            } catch (e) {
                console.error('Failed to restore booking state', e);
                sessionStorage.removeItem('bookingRedirect');
            }
        }
    }, [packageData]);

    // Prevent body scroll when modal is open
    useEffect(() => {
        if (isModalOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }
        return () => { document.body.style.overflow = 'unset'; };
    }, [isModalOpen]);

    // Close modal on outside click
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (!isSubmitting && modalRef.current && !modalRef.current.contains(e.target)) {
                setIsModalOpen(false);
            }
        };
        if (isModalOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => { document.removeEventListener('mousedown', handleClickOutside); };
    }, [isModalOpen, isSubmitting]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleMealPlanSelect = (planId) => {
        const plan = mealPlans.find(p => p.id === planId);
        setSelectedMealPlan(plan);
        setSelectedVegItems([]);
        setSelectedNonVegItems([]);
        setFormData((prev) => ({
            ...prev,
            guests: plan
                ? Math.max(
                    Number(plan.min_persons) || 1,
                    Math.min(
                        Number(prev.guests) || Number(plan.min_persons) || 1,
                        Number(plan.max_persons) || Infinity
                    )
                )
                : '',
        }));
    };

    const toggleVegItem = (itemId) => {
        if (!selectedMealPlan) return;
        const maxVeg = selectedMealPlan.max_veg_items || 0;
        const isSelected = selectedVegItems.includes(itemId);
        if (isSelected) {
            setSelectedVegItems(prev => prev.filter(id => id !== itemId));
        } else {
            if (selectedVegItems.length >= maxVeg) {
                setError(`You can select at most ${maxVeg} veg items.`);
                return;
            }
            setSelectedVegItems(prev => [...prev, itemId]);
            setError(null);
        }
    };

    const toggleNonVegItem = (itemId) => {
        if (!selectedMealPlan) return;
        const maxNonVeg = selectedMealPlan.max_nonveg_items || 0;
        const isSelected = selectedNonVegItems.includes(itemId);
        if (isSelected) {
            setSelectedNonVegItems(prev => prev.filter(id => id !== itemId));
        } else {
            if (selectedNonVegItems.length >= maxNonVeg) {
                setError(`You can select at most ${maxNonVeg} non‑veg items.`);
                return;
            }
            setSelectedNonVegItems(prev => [...prev, itemId]);
            setError(null);
        }
    };

    const handleSpecialRequestToggle = (requestId) => {
        setSelectedSpecialRequests(prev => {
            const exists = prev.find(r => r.id === requestId);
            if (exists) {
                return prev.filter(r => r.id !== requestId);
            } else {
                return [...prev, { id: requestId, quantity: 1 }];
            }
        });
    };

    const handleSpecialRequestQuantity = (requestId, change) => {
        setSelectedSpecialRequests(prev =>
            prev.map(r => {
                if (r.id === requestId) {
                    const newQty = Math.max(1, (r.quantity || 1) + change);
                    return { ...r, quantity: newQty };
                }
                return r;
            })
        );
    };

    const handleNext = () => {
        if (step === 1) {
            if (!formData.name.trim() || !formData.email.trim() || !formData.phone.trim()) {
                setError('Please fill in all required contact fields.');
                return;
            }
            if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
                setError('Please enter a valid email address.');
                return;
            }
            setError(null);
        }
        if (step === 2) {
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            if (!formData.date || new Date(`${formData.date}T00:00:00`) < today) {
                setError('Please select today or a future event date.');
                return;
            }
            if (!selectedMealPlan) {
                setError('Please select a meal plan.');
                return;
            }
            const guests = Number(formData.guests);
            if (
                !Number.isInteger(guests) ||
                guests < Number(selectedMealPlan.min_persons) ||
                guests > Number(selectedMealPlan.max_persons)
            ) {
                setError(`Guest count must be between ${selectedMealPlan.min_persons} and ${selectedMealPlan.max_persons}.`);
                return;
            }
            setError(null);
        }
        if (step < 3) setStep(step + 1);
    };

    const handlePrev = () => {
        if (step > 1) setStep(step - 1);
        setError(null);
    };

    // ---- Book Now with auth check ----
    const handleBookNow = () => {
        const token = localStorage.getItem('accessTok');
        if (!token) {
            alert('No admin session found. Please sign in to the admin panel and try again.');
            return;
        }
        setError(null);
        setIsModalOpen(true);
    };

    // ---- Submit booking (ONLY runs when Confirm button is clicked on step 3) ----
    const handleSubmit = async (e) => {
        if (e && e.preventDefault) e.preventDefault();

        if (step !== 3) {
            return;
        }

        if (isSubmitting) return;

        // Re-validate everything right before hitting the API.
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        if (!formData.name.trim() || !formData.email.trim() || !formData.phone.trim()) {
            setError('Please fill in all required contact fields.');
            return;
        }
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
            setError('Please enter a valid email address.');
            return;
        }
        if (!formData.date || new Date(`${formData.date}T00:00:00`) < today) {
            setError('Please select today or a future event date.');
            return;
        }
        if (!selectedMealPlan) {
            setError('Please select a meal plan.');
            return;
        }
        const guestsNum = Number(formData.guests);
        if (
            !Number.isInteger(guestsNum) ||
            guestsNum < Number(selectedMealPlan.min_persons) ||
            guestsNum > Number(selectedMealPlan.max_persons)
        ) {
            setError(`Guest count must be between ${selectedMealPlan.min_persons} and ${selectedMealPlan.max_persons}.`);
            return;
        }

        const token = localStorage.getItem('accessTok');
        if (!token) {
            setError('Your admin session has expired. Please sign in again.');
            return;
        }

        setIsSubmitting(true);
        setError(null);

        const payload = {
            package_id: packageData.id,
            head_count: parseInt(formData.guests, 10),
            event_date: formData.date,
            contact_name: formData.name.trim(),
            contact_email: formData.email.trim(),
            contact_mobile: formData.phone.trim(),
            contact_whatsapp: formData.whatsapp.trim() || formData.phone.trim(),
            notes: formData.notes.trim(),
            meal_plan_id: selectedMealPlan?.id,
            menu_selections: {
                veg: selectedVegItems,
                nonveg: selectedNonVegItems,
            },
            special_requests: selectedSpecialRequests.map(r => ({
                id: r.id,
                quantity: r.quantity || 1,
            })),
            activities: [],
        };

        try {
            const bookingResponse = await api.post('/admin/wedding/bookings', payload, {
                headers: { "X-ADMIN-TOKEN": token },
            });

            const bookingResultData = bookingResponse.data;
            if (!bookingResultData.status) {
                throw new Error(bookingResultData.message || 'Booking creation failed.');
            }

            // Booking confirmed — redirect to admin dashboard.
            window.location.href = "https://admintest.jayrajgroup.org/admin/wedding-bookings";
        } catch (err) {
            if (err.response?.status === 401) {
                localStorage.removeItem('accessTok');
                alert("Your admin token is invalid or expired. Please log in again.");
            } else {
                console.error('Booking submission error:', err);
                setError(err.response?.data?.message || 'An error occurred. Please try again.');
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    const resetForm = () => {
        setStep(1);
        setFormData({ name: '', email: '', phone: '', whatsapp: '', guests: '', date: '', notes: '' });
        setSelectedMealPlan(null);
        setSelectedSpecialRequests([]);
        setSelectedVegItems([]);
        setSelectedNonVegItems([]);
        setIsSubmitting(false);
        setError(null);
        sessionStorage.removeItem('bookingRedirect');
    };

    const hasVegMenu = selectedMealPlan?.has_veg_menu && selectedMealPlan?.veg_menu?.length > 0;
    const hasNonVegMenu = selectedMealPlan?.has_nonveg_menu && selectedMealPlan?.nonveg_menu?.length > 0;
    const formatPrice = (price) => {
        if (price === null || price === undefined || price === '' || !Number.isFinite(Number(price))) {
            return 'Price unavailable';
        }
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            maximumFractionDigits: 0,
        }).format(Number(price));
    };

    const handleFormKeyDown = (e) => {
        if (e.key === 'Enter' && step !== 3) {
            if (e.target.tagName === 'TEXTAREA') return;
            e.preventDefault();
        }
    };

    return (
        <>
            {/* Sidebar */}
            <div className="pkgd-booking-sidebar">
                <div className="pkgd-booking-card">
                    <h3>Book This Package</h3>
                    <div className="pkgd-booking-quick-info">
                        <div className="pkgd-booking-info-item">
                            <FiCalendar />
                            <span>{packageData?.duration_days} Days</span>
                        </div>
                        <div className="pkgd-booking-info-item">
                            <FiClock />
                            <span>{packageData?.duration_nights} Nights</span>
                        </div>
                        <div className="pkgd-booking-info-item">
                            <FiMapPin />
                            <span>{destinationName}</span>
                        </div>
                    </div>

                    <button className="pkgd-btn-book" onClick={handleBookNow}>
                        Book Now <FiChevronRight />
                    </button>

                    <p className="pkgd-booking-note">
                        <FiInfo />
                        Manual admin booking - no online payment required
                    </p>
                </div>
            </div>

            {/* Modal */}
            {isModalOpen && (
                <div className="pkgd-modal-overlay">
                    <div className="pkgd-modal-container" ref={modalRef}>
                        <button
                            className="pkgd-modal-close"
                            disabled={isSubmitting}
                            onClick={() => {
                                setIsModalOpen(false);
                                resetForm();
                            }}
                        >
                            <FiX />
                        </button>

                        <div className="pkgd-modal-header">
                            <h2>Book Your Dream Wedding</h2>
                            <p>
                                <strong>{packageData?.name}</strong> at {destinationName}
                            </p>
                        </div>

                        <div className="pkgd-modal-steps">
                            <div className={`pkgd-step-indicator ${step >= 1 ? 'pkgd-active' : ''}`}>
                                <span>1</span>
                                <label>Contact</label>
                            </div>
                            <div className={`pkgd-step-indicator ${step >= 2 ? 'pkgd-active' : ''}`}>
                                <span>2</span>
                                <label>Customize</label>
                            </div>
                            <div className={`pkgd-step-indicator ${step >= 3 ? 'pkgd-active' : ''}`}>
                                <span>3</span>
                                <label>Confirm</label>
                            </div>
                        </div>

                        {error && (
                            <div className="pkgd-error-message">
                                <FiAlertCircle />
                                <span>{error}</span>
                            </div>
                        )}

                        <form
                            onSubmit={(e) => e.preventDefault()}
                            onKeyDown={handleFormKeyDown}
                            className="pkgd-modal-form"
                        >
                            {step === 1 && (
                                <div className="pkgd-form-step">
                                    <div className="pkgd-form-group">
                                        <label><FiUser /> Full Name <span>*</span></label>
                                        <input
                                            type="text"
                                            name="name"
                                            value={formData.name}
                                            onChange={handleChange}
                                            placeholder="Your full name"
                                            required
                                        />
                                    </div>
                                    <div className="pkgd-form-group">
                                        <label><FiMail /> Email <span>*</span></label>
                                        <input
                                            type="email"
                                            name="email"
                                            value={formData.email}
                                            onChange={handleChange}
                                            placeholder="you@example.com"
                                            required
                                        />
                                    </div>
                                    <div className="pkgd-form-group">
                                        <label><FiPhone /> Phone <span>*</span></label>
                                        <input
                                            type="tel"
                                            name="phone"
                                            value={formData.phone}
                                            onChange={handleChange}
                                            placeholder="+91 98765 43210"
                                            required
                                        />
                                    </div>
                                    <div className="pkgd-form-group">
                                        <label><FiMessageSquare /> WhatsApp (optional)</label>
                                        <input
                                            type="tel"
                                            name="whatsapp"
                                            value={formData.whatsapp}
                                            onChange={handleChange}
                                            placeholder="+91 98765 43210"
                                        />
                                    </div>
                                </div>
                            )}

                            {step === 2 && (
                                <div className="pkgd-form-step">
                                    <div className="pkgd-form-group">
                                        <label><FiCalendar /> Event Date <span>*</span></label>
                                        <input
                                            type="date"
                                            name="date"
                                            value={formData.date}
                                            onChange={handleChange}
                                            min={(() => {
                                                const today = new Date();
                                                return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
                                            })()}
                                            required
                                        />
                                    </div>

                                    <div className="pkgd-form-group">
                                        <label><FiStar /> Select Meal Plan <span>*</span></label>
                                        <select
                                            value={selectedMealPlan?.id || ''}
                                            onChange={(e) => handleMealPlanSelect(parseInt(e.target.value))}
                                            required
                                        >
                                            <option value="">Choose a meal plan</option>
                                            {mealPlans.map((plan) => (
                                                <option key={plan.id} value={plan.id}>
                                                    {plan.plan_name} - ₹{plan.price_per_person}/person
                                                </option>
                                            ))}
                                        </select>
                                        {selectedMealPlan && (
                                            <div className="pkgd-meal-preview">
                                                <p><strong>{selectedMealPlan.plan_name}</strong></p>
                                                <p>{selectedMealPlan.description}</p>
                                                <p>Min {selectedMealPlan.min_persons} - Max {selectedMealPlan.max_persons} guests</p>
                                            </div>
                                        )}
                                    </div>

                                    {selectedMealPlan && (
                                        <div className="pkgd-form-group pkgd-menu-selection">
                                            <label>Select Menu Items</label>
                                            <div className="pkgd-menu-categories">
                                                {hasVegMenu && (
                                                    <div className="pkgd-menu-category">
                                                        <h5>Veg Items (max {selectedMealPlan.max_veg_items})</h5>
                                                        <div className="pkgd-menu-items">
                                                            {selectedMealPlan.veg_menu.map((item) => {
                                                                const isSelected = selectedVegItems.includes(item.id);
                                                                const isDisabled = !isSelected && selectedVegItems.length >= selectedMealPlan.max_veg_items;
                                                                return (
                                                                    <label key={item.id} className="pkgd-menu-item">
                                                                        <input
                                                                            type="checkbox"
                                                                            checked={isSelected}
                                                                            onChange={() => toggleVegItem(item.id)}
                                                                            disabled={isDisabled}
                                                                        />
                                                                        <span><strong>{item.item_name}</strong> – {item.category_label}</span>
                                                                        {item.description && <small>({item.description})</small>}
                                                                    </label>
                                                                );
                                                            })}
                                                        </div>
                                                        <span className="pkgd-menu-count">
                                                            Selected: {selectedVegItems.length} / {selectedMealPlan.max_veg_items}
                                                        </span>
                                                    </div>
                                                )}
                                                {hasNonVegMenu && (
                                                    <div className="pkgd-menu-category">
                                                        <h5>Non‑Veg Items (max {selectedMealPlan.max_nonveg_items})</h5>
                                                        <div className="pkgd-menu-items">
                                                            {selectedMealPlan.nonveg_menu.map((item) => {
                                                                const isSelected = selectedNonVegItems.includes(item.id);
                                                                const isDisabled = !isSelected && selectedNonVegItems.length >= selectedMealPlan.max_nonveg_items;
                                                                return (
                                                                    <label key={item.id} className="pkgd-menu-item">
                                                                        <input
                                                                            type="checkbox"
                                                                            checked={isSelected}
                                                                            onChange={() => toggleNonVegItem(item.id)}
                                                                            disabled={isDisabled}
                                                                        />
                                                                        <span><strong>{item.item_name}</strong> – {item.category_label}</span>
                                                                        {item.description && <small>({item.description})</small>}
                                                                    </label>
                                                                );
                                                            })}
                                                        </div>
                                                        <span className="pkgd-menu-count">
                                                            Selected: {selectedNonVegItems.length} / {selectedMealPlan.max_nonveg_items}
                                                        </span>
                                                    </div>
                                                )}
                                                {!hasVegMenu && !hasNonVegMenu && (
                                                    <p className="pkgd-menu-empty">No menu items available for this plan.</p>
                                                )}
                                            </div>
                                        </div>
                                    )}

                                    {specialRequests && specialRequests.length > 0 && (
                                        <div className="pkgd-form-group">
                                            <label>Special Requests</label>
                                            <div className="pkgd-special-list">
                                                {specialRequests.map((req) => {
                                                    const selected = selectedSpecialRequests.find(r => r.id === req.id);
                                                    return (
                                                        <div key={req.id} className="pkgd-special-item">
                                                            <label className="pkgd-checkbox-label">
                                                                <input
                                                                    type="checkbox"
                                                                    checked={!!selected}
                                                                    onChange={() => handleSpecialRequestToggle(req.id)}
                                                                />
                                                                <span>{req.title || `Request #${req.id}`}</span>
                                                            </label>
                                                            {selected && (
                                                                <div className="pkgd-quantity-control">
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => handleSpecialRequestQuantity(req.id, -1)}
                                                                    >
                                                                        <FiMinus />
                                                                    </button>
                                                                    <span>{selected.quantity || 1}</span>
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => handleSpecialRequestQuantity(req.id, 1)}
                                                                    >
                                                                        <FiPlus />
                                                                    </button>
                                                                </div>
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    )}
                                    <div className="pkgd-form-group">
                                        <label><FiUsers /> Number of Guests <span>*</span></label>
                                        <input
                                            type="number"
                                            name="guests"
                                            value={formData.guests}
                                            onChange={handleChange}
                                            placeholder="Enter number of guests"
                                            min={selectedMealPlan?.min_persons}
                                            max={selectedMealPlan?.max_persons}
                                            required
                                        />
                                    </div>

                                    <div className="pkgd-form-group">
                                        <label><FiMessageSquare /> Additional Notes</label>
                                        <textarea
                                            name="notes"
                                            value={formData.notes}
                                            onChange={handleChange}
                                            placeholder="Any special requirements or preferences..."
                                            rows="3"
                                        />
                                    </div>
                                </div>
                            )}

                            {step === 3 && (
                                <div className="pkgd-form-step pkgd-confirmation-step">
                                    <div className="pkgd-confirmation-box">
                                        <h4>Review Your Booking</h4>
                                        <div className="pkgd-confirmation-details">
                                            <div className="pkgd-conf-item">
                                                <span className="pkgd-conf-label">Package</span>
                                                <span className="pkgd-conf-value">{packageData?.name}</span>
                                            </div>
                                            <div className="pkgd-conf-item">
                                                <span className="pkgd-conf-label">Destination</span>
                                                <span className="pkgd-conf-value">{destinationName}</span>
                                            </div>
                                            <div className="pkgd-conf-item">
                                                <span className="pkgd-conf-label">Duration</span>
                                                <span className="pkgd-conf-value">
                                                    {packageData?.duration_days} Days, {packageData?.duration_nights} Nights
                                                </span>
                                            </div>
                                            <div className="pkgd-conf-item">
                                                <span className="pkgd-conf-label">Name</span>
                                                <span className="pkgd-conf-value">{formData.name}</span>
                                            </div>
                                            <div className="pkgd-conf-item">
                                                <span className="pkgd-conf-label">Email</span>
                                                <span className="pkgd-conf-value">{formData.email}</span>
                                            </div>
                                            <div className="pkgd-conf-item">
                                                <span className="pkgd-conf-label">Phone</span>
                                                <span className="pkgd-conf-value">{formData.phone}</span>
                                            </div>
                                            {formData.whatsapp && (
                                                <div className="pkgd-conf-item">
                                                    <span className="pkgd-conf-label">WhatsApp</span>
                                                    <span className="pkgd-conf-value">{formData.whatsapp}</span>
                                                </div>
                                            )}
                                            <div className="pkgd-conf-item">
                                                <span className="pkgd-conf-label">Event Date</span>
                                                <span className="pkgd-conf-value">
                                                    {new Date(formData.date).toLocaleDateString('en-IN', {
                                                        day: 'numeric',
                                                        month: 'long',
                                                        year: 'numeric',
                                                    })}
                                                </span>
                                            </div>
                                            <div className="pkgd-conf-item">
                                                <span className="pkgd-conf-label">Guests</span>
                                                <span className="pkgd-conf-value">{formData.guests}</span>
                                            </div>
                                            {selectedMealPlan && (
                                                <>
                                                    <div className="pkgd-conf-item">
                                                        <span className="pkgd-conf-label">Meal Plan</span>
                                                        <span className="pkgd-conf-value">{selectedMealPlan.plan_name}</span>
                                                    </div>
                                                    <div className="pkgd-conf-item">
                                                        <span className="pkgd-conf-label">Meal Plan Rate</span>
                                                        <span className="pkgd-conf-value">
                                                            {formatPrice(selectedMealPlan.price_per_person)} per guest
                                                        </span>
                                                    </div>
                                                    <div className="pkgd-conf-item">
                                                        <span className="pkgd-conf-label">Estimated Meal Total</span>
                                                        <span className="pkgd-conf-value">
                                                            {formatPrice(
                                                                Number(selectedMealPlan.price_per_person) * Number(formData.guests)
                                                            )}
                                                        </span>
                                                    </div>
                                                    {selectedVegItems.length > 0 && (
                                                        <div className="pkgd-conf-item">
                                                            <span className="pkgd-conf-label">Veg Items</span>
                                                            <span className="pkgd-conf-value">
                                                                {selectedVegItems.map(id => {
                                                                    const item = selectedMealPlan.veg_menu.find(i => i.id === id);
                                                                    return item ? item.item_name : id;
                                                                }).join(', ')}
                                                            </span>
                                                        </div>
                                                    )}
                                                    {selectedNonVegItems.length > 0 && (
                                                        <div className="pkgd-conf-item">
                                                            <span className="pkgd-conf-label">Non‑Veg Items</span>
                                                            <span className="pkgd-conf-value">
                                                                {selectedNonVegItems.map(id => {
                                                                    const item = selectedMealPlan.nonveg_menu.find(i => i.id === id);
                                                                    return item ? item.item_name : id;
                                                                }).join(', ')}
                                                            </span>
                                                        </div>
                                                    )}
                                                </>
                                            )}
                                            {selectedSpecialRequests.length > 0 && (
                                                <div className="pkgd-conf-item">
                                                    <span className="pkgd-conf-label">Special Requests</span>
                                                    <span className="pkgd-conf-value">
                                                        {selectedSpecialRequests.map((request) => {
                                                            const requestDetails = specialRequests.find((item) => item.id === request.id);
                                                            return `${requestDetails?.title || `Request #${request.id}`} × ${request.quantity}`;
                                                        }).join(', ')}
                                                    </span>
                                                </div>
                                            )}
                                            {formData.notes && (
                                                <div className="pkgd-conf-item">
                                                    <span className="pkgd-conf-label">Notes</span>
                                                    <span className="pkgd-conf-value">{formData.notes}</span>
                                                </div>
                                            )}
                                        </div>
                                        <div className="pkgd-confirmation-note">
                                            <FiAlertCircle />
                                            <span>This creates an admin booking. No payment is collected here.</span>
                                        </div>
                                    </div>
                                </div>
                            )}

                            <div className="pkgd-modal-actions">
                                {step > 1 && (
                                    <button type="button" className="pkgd-btn-prev" onClick={handlePrev}>
                                        Back
                                    </button>
                                )}
                                {step < 3 ? (
                                    <button type="button" className="pkgd-btn-next" onClick={handleNext}>
                                        Next Step <FiChevronRight />
                                    </button>
                                ) : (
                                    <button type="button" className="pkgd-btn-submit" onClick={handleSubmit} disabled={isSubmitting}>
                                        {isSubmitting ? 'Creating booking...' : <>
                                            <FiCheckCircle />
                                            Confirm & Create Booking
                                        </>}
                                    </button>
                                )}
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </>
    );
}